import { instance } from "./axios-service";

type GeocodePayload = {
  lat?: number | string;
  lon?: number | string;
  lng?: number | string;
  latitude?: number | string;
  longitude?: number | string;
};

function parseNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function extractCoordinates(payload: unknown): [number, number] | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const result = payload as GeocodePayload;
  const latitude = parseNumber(result.lat ?? result.latitude);
  const longitude = parseNumber(result.lon ?? result.lng ?? result.longitude);

  if (latitude === null || longitude === null) {
    return null;
  }

  return [latitude, longitude];
}

export async function geocodeAddress(address: string): Promise<[number, number] | null> {
  try {
    const response = await instance.get("/geocode/search", {
      params: { address },
    });

    return extractCoordinates(response.data);
  } catch (_) {
    return null;
  }
}

export async function geocodeAddressesBatch(addresses: string[]): Promise<([number, number] | null)[]> {
  if (addresses.length === 0) {
    return [];
  }

  try {
    const response = await instance.post("/geocode/batch", {
      addresses,
    });

    if (!Array.isArray(response.data)) {
      return addresses.map(() => null);
    }

    const results = response.data.map((item: unknown) => extractCoordinates(item));

    if (results.length < addresses.length) {
      return [...results, ...addresses.slice(results.length).map(() => null)];
    }

    return results.slice(0, addresses.length);
  } catch (_) {
    return addresses.map(() => null);
  }
}
